import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

/**
 * Merge Tailwind classes without conflicts.
 * Combines clsx (conditional classes) with tailwind-merge (dedup utilities).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format an ISO date string for display.
 * @param dateStr  ISO-8601 date string
 * @param pattern  date-fns format pattern (default: "MMM d, yyyy")
 */
export function formatDate(dateStr: string, pattern = "MMM d, yyyy"): string {
  try {
    return format(parseISO(dateStr), pattern);
  } catch {
    return dateStr;
  }
}

/** True when an ISO string includes a non-midnight time (not date-only). */
function isoHasMeaningfulTime(dateStr: string): boolean {
  const s = dateStr.trim();
  const tIdx = s.indexOf("T");
  if (tIdx < 0) return false;
  const timePart = s.slice(tIdx + 1).replace(/Z$/i, "").split(/[+-]/)[0] ?? "";
  return !/^00:00:00(\.0+)?$/.test(timePart);
}

/**
 * Format an ISO date/time for display.
 * Date-only or midnight placeholders show as date; real times include clock time.
 */
export function formatOptionalDateTime(
  dateStr: string,
  datePattern = "MMM d, yyyy",
  dateTimePattern = "MMM d, yyyy · h:mm a",
): string {
  try {
    const hasTime = isoHasMeaningfulTime(dateStr);
    return format(parseISO(dateStr), hasTime ? dateTimePattern : datePattern);
  } catch {
    return dateStr;
  }
}

/** Format clock time when the ISO string has a non-midnight time; otherwise null. */
export function formatOptionalTime(dateStr: string, pattern = "h:mm a"): string | null {
  try {
    if (!isoHasMeaningfulTime(dateStr)) return null;
    return format(parseISO(dateStr), pattern);
  } catch {
    return null;
  }
}

/** Format an ISO date-time for display (date + time). */
export function formatDateTime(dateStr: string, pattern = "MMM d, yyyy · h:mm a"): string {
  try {
    return format(parseISO(dateStr), pattern);
  } catch {
    return dateStr;
  }
}

/** Currency for every amount the app shows: Ghana cedis unless the build sets VITE_CURRENCY. */
export const APP_CURRENCY = (import.meta.env.VITE_CURRENCY as string | undefined) || "GHS";
const APP_LOCALE = (import.meta.env.VITE_LOCALE as string | undefined) || "en-GH";

/** Format an amount in the app currency, e.g. 1234.5 → "GH₵1,234.50". */
export function formatCurrency(amount: number, opts: { decimals?: number } = {}): string {
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    ...(opts.decimals != null ? { minimumFractionDigits: opts.decimals, maximumFractionDigits: opts.decimals } : {}),
  }).format(Number.isFinite(amount) ? amount : 0);
}

/** Format minor units (pesewas / cents), e.g. 12345 → "GH₵123.45". */
export function formatCents(cents: number, opts: { decimals?: number } = {}): string {
  return formatCurrency((Number.isFinite(cents) ? cents : 0) / 100, opts);
}

/** The currency symbol on its own, for input prefixes ("GH₵"). */
export function currencySymbol(): string {
  return (
    new Intl.NumberFormat(APP_LOCALE, { style: "currency", currency: APP_CURRENCY })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value ?? APP_CURRENCY
  );
}

/**
 * Format a Medical Record Number for display (e.g., "MRN-XXXX-XXXX").
 */
export function formatMRN(mrn: string): string {
  if (!mrn) return "";
  const digits = mrn.replace(/\D/g, "");
  const padded = digits.padStart(8, "0");
  return `MRN-${padded.slice(0, 4)}-${padded.slice(4, 8)}`;
}

/**
 * Get initials from first and last name (e.g., "John Doe" → "JD").
 */
export function getInitials(firstName: string, lastName: string): string {
  const first = firstName.trim().charAt(0).toUpperCase();
  const last = lastName.trim().charAt(0).toUpperCase();
  return `${first}${last}`;
}

/**
 * Calculate age from DOB string.
 */
export function calculateAge(dateStr: string): number {
  try {
    const birthday = parseISO(dateStr);
    const ageDifMs = Date.now() - birthday.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  } catch {
    return 0;
  }
}

/**
 * Calculate days between a past date and now.
 */
export function calculateDaysAgo(dateStr: string): number {
  try {
    const past = parseISO(dateStr);
    const diffTime = Math.abs(Date.now() - past.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
}
